import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';

// Components
const Navbar = React.lazy(() => import('./components/Navbar'));
import Hero from './components/Hero';
const IndiaMap = React.lazy(() => import('./components/IndiaMap'));
const ServicesSlider = React.lazy(() => import('./components/ServicesSlider'));
const TestimonialsSlider = React.lazy(() => import('./components/TestimonialsSlider'));
const AboutSection = React.lazy(() => import('./components/AboutSection'));
const Footer = React.lazy(() => import('./components/Footer'));
const ChatBot = React.lazy(() => import('./components/ChatBot'));
const DemoRequestModal = React.lazy(() => import('./components/DemoRequestModal'));
const LanguagePopup = React.lazy(() => import('./components/LanguagePopup'));
const ScrollToTop = React.lazy(() => import('./components/ScrollToTop'));


// Lazy Loaded Pages
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const BlogsPage = React.lazy(() => import('./pages/BlogsPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const VendorOnboardingPage = React.lazy(() => import('./pages/VendorOnboardingPage'));
const VendorFormPage = React.lazy(() => import('./pages/VendorFormPage')); // Kept if needed, though not in original list
const ReferFriendPage = React.lazy(() => import('./pages/ReferFriendPage'));
const APIServicesPage = React.lazy(() => import('./pages/APIServicesPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const BrochurePage = React.lazy(() => import('./pages/BrochurePage'));

// Lazy Loaded Policies
const PrivacyPolicy = React.lazy(() => import('./pages/policies/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/policies/TermsOfService'));
const CookiePolicy = React.lazy(() => import('./pages/policies/CookiePolicy'));
const RefundPolicy = React.lazy(() => import('./pages/policies/RefundPolicy'));

// Data


const Home = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  React.useEffect(() => {
    if (window.location.hash === '#about') {
      const element = document.getElementById('about');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-linen-beige">
      <React.Suspense fallback={<div className="h-20 bg-white" />}>
        {/* Navigation */}
        <Navbar onRequestDemo={() => setIsDemoModalOpen(true)} />
      </React.Suspense>

      {/* Hero Section */}
      <Hero onRequestDemo={() => setIsDemoModalOpen(true)} />

      {/* Services Section */}
      <section id="services">
        <React.Suspense fallback={<div className="h-96 bg-gray-50 animate-pulse" />}>
          <ServicesSlider />
        </React.Suspense>

      </section>

      {/* India Map Section */}
      <section id="coverage">
        <React.Suspense fallback={<div className="h-[500px] bg-white animate-pulse" />}>
          <IndiaMap />
        </React.Suspense>

      </section>

      {/* Testimonials Section */}
      <section id="testimonials">
        <React.Suspense fallback={<div className="h-80 bg-gray-50 animate-pulse" />}>
          <TestimonialsSlider />
        </React.Suspense>
      </section>

      {/* About Section */}
      <React.Suspense fallback={<div className="h-96 bg-white animate-pulse" />}>
        <AboutSection />
      </React.Suspense>

      {/* Footer */}
      <React.Suspense fallback={<div className="h-40 bg-ebony" />}>
        <Footer />
      </React.Suspense>

      <React.Suspense fallback={null}>
        {/* Demo Request Modal */}
        <DemoRequestModal
          isOpen={isDemoModalOpen}
          onClose={() => setIsDemoModalOpen(false)}
        />
      </React.Suspense>

    </div>
  );
};

const GlobalWidgets = ({ isChatBotOpen, setIsChatBotOpen }) => {
  const location = useLocation();
  const isBrochure = location.pathname === '/company-brochure' || location.pathname === '/company-brochure/';

  // Canonical Tag Management
  React.useEffect(() => {
    const canonicalUrl = `https://matrica.co.in${location.pathname === '/' ? '' : location.pathname}`;
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);
  }, [location]);

  if (isBrochure) return null;

  return (
    <React.Suspense fallback={null}>
      <LanguagePopup isHidden={isChatBotOpen} />
      <ChatBot isOpen={isChatBotOpen} onToggle={() => setIsChatBotOpen(!isChatBotOpen)} />
    </React.Suspense>
  );
};

import Lenis from 'lenis';


function App() {
  React.useEffect(() => {
    // Initialize Lenis for smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      // Lenis cleanup if necessary, but usually globally active
      lenis.destroy();
    };
  }, []);

  const [isChatBotOpen, setIsChatBotOpen] = useState(false);

  return (
    <div className="App">
      <BrowserRouter>
        <React.Suspense fallback={null}>
          <ScrollToTop />
        </React.Suspense>

        <React.Suspense fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-hunter-green"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blogs/:id" element={<BlogsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/vendor-onboarding" element={<VendorOnboardingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/refer-friend" element={<ReferFriendPage />} />
            <Route path="/digital-services" element={<APIServicesPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/company-brochure" element={<BrochurePage />} />
          </Routes>
        </React.Suspense>
        <GlobalWidgets isChatBotOpen={isChatBotOpen} setIsChatBotOpen={setIsChatBotOpen} />
        <Toaster

          position="top-center"
          reverseOrder={false}
          toastOptions={{
            style: {
              background: '#004F30', // Hunter Green
              color: '#FFFFFF',
              zIndex: 99999,
              borderRadius: '12px',
              padding: '16px 24px',
              boxShadow: '0 10px 30px -10px rgba(0, 79, 48, 0.4)',
              fontSize: '15px',
              fontWeight: '500',
            },
            success: {
              duration: 4000,
              iconTheme: {
                primary: '#fff',
                secondary: '#004F30',
              },
            },
            error: {
              duration: 5000,
              style: {
                background: '#EF4444', // Keep error red for clarity, or ask user? User said "make its colour hunter green", implies success/general. I'll style global as green.
                color: '#FFFFFF',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#EF4444',
              },
            },
          }}
          containerStyle={{
            zIndex: 99999, // Ensure container is on top
            top: '6rem', // Offset to appear below Navbar
            position: 'fixed' // Ensure it stays visible on scroll
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;